import { createFileRoute } from '@tanstack/react-router';
import { assertPublicToolsEnabled } from '@/lib/public-tools-feature';
import { SeoLayout } from '@/components/astro/new-design-25/SEO/seo-layout';
import { seo } from '@/seo/seo';

const heroTitle = 'AI-Powered Study Tool for Students';
const heroDescription =
  'Transform your study sessions with audio learning. Listen to textbooks, papers, and study materials while walking, exercising, or commuting.';

const featureTitle = heroTitle;
const featureSubtitle = 'Study smarter, not harder';
const featureDescription = heroDescription;
const featureBadge = 'Student Favorite';
const featureVariant = 'default';
const featureCtaText = 'Start Studying';

const featureFeatures = [
  {
    title: 'Multitasking Study Sessions',
    description:
      'Listen to study materials while walking, exercising, cooking, or during your daily commute.',
  },
  {
    title: 'Retention Enhancement',
    description:
      'Audio learning helps improve retention and allows you to review material multiple times easily.',
  },
  {
    title: 'Flexible Study Schedule',
    description:
      'Turn any dead time into productive study time with mobile-friendly audio content.',
  },
];

const comparisonTableData = {
  title: 'Study Method Comparison Table',
  description:
    'See how audio learning compares to traditional study methods across key factors.',
  columns: [
    { header: 'Audio Learning', subheader: 'Listening', isHighlighted: true },
    { header: 'Flashcards', subheader: 'Quizlet/Anki' },
    { header: 'Traditional Reading' },
  ],
  rows: [
    {
      feature: 'Multitasking',
      description: 'Ability to study while doing other activities.',
      values: [
        'Walk, exercise, commute',
        'Requires screen focus',
        'Requires text focus',
      ],
    },
    {
      feature: 'Complex Content',
      description:
        'How well the method handles comprehensive academic material.',
      values: [
        'Preserves full context',
        'Reduces to fragments',
        'Good but slow',
      ],
    },
    {
      feature: 'Time Efficiency',
      description: 'How efficiently you can use available time for studying.',
      values: [
        'Study during day time',
        'Needs dedicated study time',
        'Needs dedicated time',
      ],
    },
    {
      feature: 'Content Type',
      description: 'Types of academic content best suited for each method.',
      values: [
        'Research papers, textbooks, articles',
        'Facts, vocabulary, definitions',
        'Any text material',
      ],
    },
    {
      feature: 'Learning Style',
      description: 'Which learning preferences each method serves best.',
      values: [
        'Perfect for auditory learners',
        'Visual/kinesthetic learners',
        'Visual learners',
      ],
    },
    {
      feature: 'Setup Time',
      description: 'Initial effort required to start using the method.',
      values: ['Upload and listen', 'Create cards or find sets', 'None'],
    },
    {
      feature: 'Accessibility',
      description: 'Support for students with different learning needs.',
      values: [
        'Great for learning disabilities',
        'Limited accessibility',
        'Limited accessibility',
      ],
    },
  ],
};

const useCasesData = {
  title: 'Student Type Use Cases',
  description:
    'How different types of students use audio learning to overcome their unique study challenges.',
  useCases: [
    {
      title: 'The Commuter Student',
      challenge:
        '2-3 hours daily traveling to campus, dead time that could be productive',
      solutionSections: [
        {
          label: 'Audio Study Solution',
          content: 'Convert textbook chapters to audio, review during commute',
        },
        {
          label: 'Flashcard Limitation',
          content:
            "Can't safely use phone/cards while driving or on crowded transit",
        },
      ],
    },
    {
      title: 'The Athlete/Active Student',
      challenge: 'Limited study time between training, games, and classes',
      solutionSections: [
        {
          label: 'Audio Study Solution',
          content: 'Listen to course materials during workouts, recovery time',
        },
        {
          label: 'Flashcard Limitation',
          content:
            "Can't flip cards while exercising or during physical therapy",
        },
      ],
    },
    {
      title: 'The Working Student',
      challenge:
        'Balancing job and studies, needs to maximize limited study time',
      solutionSections: [
        {
          label: 'Audio Study Solution',
          content: 'Study during work commute, breaks, household tasks',
        },
        {
          label: 'Flashcard Limitation',
          content:
            'Requires dedicated sitting time that working students often lack',
        },
      ],
    },
    {
      title: 'The Graduate Student',
      challenge:
        'Processing 20+ research papers per week across multiple complex topics',
      solutionSections: [
        {
          label: 'Audio Study Solution',
          content:
            'Listen to full papers to understand methodology and arguments',
        },
        {
          label: 'Flashcard Limitation',
          content:
            'Cannot capture nuanced academic arguments in simple Q&A format',
        },
      ],
    },
  ],
};

const seoSections = [
  {
    title: 'Audio Learning That Works While You Move',
    content:
      'Traditional study tools like Quizlet and Anki require you to sit down, look at a screen, and actively flip through flashcards - but what about all the time you spend walking to class, commuting, or exercising? Our audio study tool transforms dead time into productive study sessions by converting your textbooks, research papers, and study materials into natural-sounding audio that you can absorb anywhere, anytime.',
    contentSecondary:
      "Unlike flashcard apps that demand your full attention and screen time, audio learning lets you multitask effectively while maintaining comprehension. Whether you're walking between classes, doing chores, or hitting the gym, you can simultaneously review course materials, process research papers, or catch up on readings. This approach is particularly powerful for auditory learners who retain information better through listening, and for busy students who need to maximize every minute of their packed schedules.",
  },
  {
    title: 'Why Audio Study Beats Flashcards for Comprehensive Learning',
    content:
      'While flashcard apps like Quizlet excel at memorizing isolated facts and vocabulary, they struggle with complex academic content that requires deeper understanding. Flashcards reduce rich information into bite-sized fragments, which works for language learning but fails for subjects requiring contextual comprehension like literature analysis, scientific concepts, or philosophical arguments. Audio study tools preserve the full context and natural flow of academic content.',
    contentSecondary:
      'The fundamental difference lies in learning approach: flashcards test recall of predetermined answers, while audio learning builds genuine understanding through exposure to complete ideas and arguments. Students using audio study consistently report better comprehension of complex materials compared to flashcard-based memorization, with testimonials noting "finally understanding concepts instead of just memorizing facts" and "much better for processing actual course readings than breaking everything into cards."',
  },
];

export const Route = createFileRoute('/_marketing/_seo/study-tool')({
  beforeLoad: assertPublicToolsEnabled,
  head: () => ({
    meta: [
      ...seo({
        title:
          'Audio Study Tool | Listen to Textbooks While Walking & Commuting | Listening',
        description:
          'Audio study tool for students. Listen to textbooks, papers, and study materials while walking, commuting, or exercising. Study smarter with audio learning.',
      }),
    ],
  }),
  component: () => (
    <SeoLayout
      comparisonTableData={comparisonTableData}
      featureBadge={featureBadge}
      featureCtaText={featureCtaText}
      featureDescription={featureDescription}
      featureFeatures={featureFeatures}
      featureSubtitle={featureSubtitle}
      featureTitle={featureTitle}
      featureVariant={featureVariant}
      pageDescription={heroDescription}
      pageTitle={heroTitle}
      seoSections={seoSections}
      useCasesData={useCasesData}
    />
  ),
});
